/**
 * Offline write queue for on-set coverage (M3.4).
 *
 * Sets have bad networks. A tap must feel instant and never be lost, so
 * every write is applied to the UI first, then either sent now or parked
 * here (localStorage) and replayed in order when the network returns.
 *
 * Only idempotent writes belong here (PUT with the full intended state);
 * replaying twice must be harmless.
 */
const KEY = 'ff-offline-queue-v1';

function read() {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; }
}
function write(items) {
  try { localStorage.setItem(KEY, JSON.stringify(items)); } catch { /* storage full or blocked: keep in memory only */ }
}

let memory = read();
const listeners = new Set();
const notify = () => listeners.forEach((fn) => { try { fn(memory.length); } catch { /* ignore */ } });

export function pendingCount() { return memory.length; }
export function subscribe(fn) { listeners.add(fn); fn(memory.length); return () => listeners.delete(fn); }

/** Park a write. Same (method,url) collapses to the latest body. */
export function enqueue({ method, url, body }) {
  memory = memory.filter((it) => !(it.method === method && it.url === url));
  memory.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, method, url, body, ts: Date.now() });
  write(memory);
  notify();
}

let flushing = false;
/**
 * Replay queued writes in order. `send({method,url,body})` performs one
 * request and must throw on failure. Stops at the first failure so order is
 * preserved; 4xx (other than 408/429) is dropped as unrecoverable.
 */
export async function flush(send) {
  if (flushing || memory.length === 0) return { sent: 0, failed: 0 };
  flushing = true;
  let sent = 0;
  let failed = 0;
  try {
    while (memory.length) {
      const it = memory[0];
      try {
        await send(it);
        memory.shift();
        sent += 1;
        write(memory);
        notify();
      } catch (err) {
        const status = err?.response?.status;
        if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
          memory.shift(); // will never succeed; drop it rather than block the queue
          failed += 1;
          write(memory);
          notify();
          continue;
        }
        break; // network / server trouble: try again later
      }
    }
  } finally {
    flushing = false;
  }
  return { sent, failed };
}

export function clearQueue() { memory = []; write(memory); notify(); }
